import React, { useEffect, useState } from 'react';
import { ViewsDropdown } from './ViewsDropdown';
import { FiltersMap } from '@wix/bex-core';
import { useToolbarCollectionContext } from '../ToolbarCollectionContext';
import { setViewsState } from '../../state/setViewsState';
import { AllItemsViewProps, Category, View } from '../../model';

export interface CollectionViewsDropdownProps<F extends FiltersMap> {
  /**
   * Array of `View` or `Category` objects that will appear as predefined views or groups of views. If you provide the array of `View` objects - they will be placed under the category `Predefined Views`.<br><br>
   * Each predefined `View` object has the following properties:<br>
   * `id` - A unique identifier (note: category IDs `saved-views` and `predefined-views` are taken by Cairo, don't use them)<br>
   * `name` - The name of the view as it appears in the dropdown<br>
   * `filters` - The filters to apply to the table when this view is selected<br>
   * `selectedColumns` - The custom columns array<br>
   * `sortDirections` - The sort directions array<br>
   * `isDeletable` - Whether it's possible to delete the view preset by users<br>
   * `isDefaultView` - Whether to automatically select the view when the table loads<br><br>
   * Each predefined `Category` object has the following properties:<br>
   * `id` - A unique identifier<br>
   * `name` - The name of the category as it appears in the dropdown<br>
   * `info` - [InfoIconProps](https://www.docs.wixdesignsystem.com/?path=/story/components-form--infoicon)<br>
   * `views`: Array of `View` objects which will appear under the category<br>
   * @external
   */
  presets?: Category<F>[] | Omit<View<F>, 'createdAt'>[];
  /**
   * A link to appear as "Learn More" button in the "Save View" modal
   * @external
   */
  learnMoreUrl?: string;
  /**
   * A function to be called after a view is saved
   * @external
   */
  onSaveViewChanges?: (view: View<F>) => void | Promise<void>;
  /**
   * A function to be called after a new view is saved
   * @external
   */
  onSaveView?: (view: View<F>) => void | Promise<void>;
  /**
   * A function to be called after a view is deleted
   * @external
   */
  onDeleteView?: (view: View<F>) => void | Promise<void>;
  /**
   * A function to be called after a view is renamed
   * @external
   */
  onRenameView?: (view: View<F>) => void | Promise<void>;
  /**
   * A function to be called after a view is set as default / reset
   * @external
   */
  onSetAsDefaultView?: (
    view: View<F>,
    isDefault: boolean,
  ) => void | Promise<void>;
  /**
   * A function to be called after a view is selected
   * @external
   */
  onSelect?: (view: View<F>) => void;
  /**
   * Shows a total count for the selected view
   * @default true
   * @external
   */
  showTotal?: boolean;
  /**
   * All items view props
   * @external
   */
  allItemsViewProps?: AllItemsViewProps<F>;
  /** @deprecated */
  hideLabel?: boolean;
  /**
   * The value to override the placeholder in the "Save View" modal
   * @external
   */
  viewNamePlaceholder?: string;
}

export const CollectionViewsDropdown = <F extends FiltersMap>(
  props: CollectionViewsDropdownProps<F>,
) => {
  const { learnMoreUrl, onSelect, showTotal, viewNamePlaceholder } = props;

  const toolbarCollectionState = useToolbarCollectionContext<unknown, F>();

  const [state] = useState(() => setViewsState(toolbarCollectionState, props));

  useEffect(() => {
    const dispose = state.init();
    const { featuredComponents } = toolbarCollectionState;

    return () => {
      featuredComponents.subToolbarExtension.set(null);
      dispose();
    };
  }, []);

  return (
    <ViewsDropdown
      dataHook="collection-views"
      state={state}
      learnMoreUrl={learnMoreUrl}
      showTotal={showTotal}
      viewNamePlaceholder={viewNamePlaceholder}
      onSelect={onSelect}
    />
  );
};

CollectionViewsDropdown.setViewsState = setViewsState;
CollectionViewsDropdown.displayName = 'CollectionViewsDropdown';
